import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  useDeletePayment,
  useRecordPayment,
  useUpdatePayment,
  useUpsertReceivableMeta,
} from "@/hooks/use-receivables";
import { newIdempotencyKey } from "@/lib/idempotency";

import {
  defaultPaymentValues,
  metaSchema,
  paymentSchema,
  type MetaFormValues,
  type PaymentFormValues,
} from "./receivable-schema";

import type { Receivable, ReceivablePayment } from "@artisancode/api-types";

export function useReceivablePaymentForm(
  open: boolean,
  receivable: Receivable | null,
) {
  const { mutate: recordPayment, isPending: isRecording } = useRecordPayment();
  const { mutate: updatePayment, isPending: isUpdating } = useUpdatePayment();
  const { mutate: deletePayment, isPending: isDeleting } = useDeletePayment();
  const { mutate: upsertMeta, isPending: isSavingMeta } =
    useUpsertReceivableMeta();

  // New key per payment entry: reused across retries of the same entry, replaced
  // once it is recorded (or the dialog moves to another sale).
  const [entry, setEntry] = useState(0);
  const saleId = receivable?.saleId;
  const idempotencyKey = useMemo(
    () => newIdempotencyKey("rpay"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, saleId, entry],
  );

  const metaForm = useForm<MetaFormValues>({
    resolver: zodResolver(metaSchema),
    defaultValues: { collectorName: "", notes: "" },
  });

  const paymentForm = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentSchema),
    defaultValues: defaultPaymentValues,
  });

  useEffect(() => {
    if (open && receivable) {
      metaForm.reset({
        collectorName: receivable.collectorName ?? "",
        notes: receivable.notes ?? "",
      });
      paymentForm.reset(defaultPaymentValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, receivable?.saleId]);

  function onSubmitMeta(values: MetaFormValues) {
    if (!receivable) return;
    upsertMeta(
      {
        saleId: receivable.saleId,
        collectorName: values.collectorName,
        notes: values.notes,
      },
      {
        onSuccess: () => toast.success("Piutang berhasil diperbarui."),
        onError: () => toast.error("Gagal menyimpan perubahan."),
      },
    );
  }

  function onSubmitPayment(values: PaymentFormValues) {
    if (!receivable) return;
    recordPayment(
      {
        saleId: receivable.saleId,
        date: values.date,
        amount: values.amount,
        method: values.method,
        verificationStatus: values.verified ? "verified" : "pending",
        notes: values.notes,
        idempotencyKey,
      },
      {
        onSuccess: () => {
          toast.success("Pembayaran berhasil dicatat.");
          setEntry((n) => n + 1);
          paymentForm.reset(defaultPaymentValues);
        },
        onError: () => toast.error("Gagal mencatat pembayaran."),
      },
    );
  }

  function toggleVerification(payment: ReceivablePayment) {
    updatePayment(
      {
        paymentId: payment.id,
        verificationStatus:
          payment.verificationStatus === "verified" ? "pending" : "verified",
      },
      { onError: () => toast.error("Gagal mengubah status verifikasi.") },
    );
  }

  function removePayment(paymentId: string) {
    deletePayment(paymentId, {
      onSuccess: () => toast.success("Pembayaran berhasil dihapus."),
      onError: () => toast.error("Gagal menghapus pembayaran."),
    });
  }

  return {
    metaForm,
    paymentForm,
    onSubmitMeta: metaForm.handleSubmit(onSubmitMeta),
    onSubmitPayment: paymentForm.handleSubmit(onSubmitPayment),
    toggleVerification,
    removePayment,
    isRecording,
    isUpdating,
    isDeleting,
    isSavingMeta,
  };
}
